import { buildAbsoluteUrl } from './canonicalUrl.js';

// JSON-LD builders. Every builder returns a plain object (or null when there
// isn't enough data to describe the thing honestly) - nothing here invents a
// value. Ratings, reviews and condition are deliberately absent: the catalogue
// carries no rating data, and it mixes new with pulled/used stock, so claiming
// either would be a false structured-data signal.

const SCHEMA = 'https://schema.org';

// Drops null/undefined/'' entries so a partial record doesn't emit empty keys.
function compact(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => {
      if (v === null || v === undefined || v === '') return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    })
  );
}

// products.stock_status -> schema.org ItemAvailability.
const AVAILABILITY = {
  in_stock: `${SCHEMA}/InStock`,
  low_stock: `${SCHEMA}/LimitedAvailability`,
  out_of_stock: `${SCHEMA}/OutOfStock`,
  pre_order: `${SCHEMA}/PreOrder`,
  special_order: `${SCHEMA}/BackOrder`,
  discontinued: `${SCHEMA}/Discontinued`,
};

const DAYS = {
  monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
  thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
};
const DAY_ORDER = Object.values(DAYS);

function to24h(hour, minute, meridiem) {
  let h = Number(hour) % 12;
  if (/pm/i.test(meridiem)) h += 12;
  return `${String(h).padStart(2, '0')}:${minute || '00'}`;
}

// Parses the free-text business-hours setting, e.g.
// "Monday - Saturday, 11:00 AM - 7:00 PM". Anything it can't parse with
// confidence returns null and the property is simply omitted - wrong opening
// hours in a rich result are worse than none.
export function parseOpeningHours(text) {
  if (!text) return null;
  const m = String(text).match(
    /^\s*(\w+)\s*(?:-|to|–)\s*(\w+)\s*,\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)\s*(?:-|to|–)\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)\s*$/i
  );
  if (!m) return null;
  const [, d1, d2, h1, min1, mer1, h2, min2, mer2] = m;
  const from = DAYS[d1.toLowerCase()];
  const to = DAYS[d2.toLowerCase()];
  if (!from || !to) return null;

  const start = DAY_ORDER.indexOf(from);
  const end = DAY_ORDER.indexOf(to);
  const days = start <= end
    ? DAY_ORDER.slice(start, end + 1)
    : [...DAY_ORDER.slice(start), ...DAY_ORDER.slice(0, end + 1)];

  return {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: days.map((d) => `${SCHEMA}/${d}`),
    opens: to24h(h1, min1, mer1),
    closes: to24h(h2, min2, mer2),
  };
}

// A handful of country names we can state as the ISO code schema.org prefers.
// Anything else is passed through as written.
const COUNTRY_CODES = { canada: 'CA', 'united states': 'US', usa: 'US' };

function postalAddress(settings) {
  // siteAddress is one free-text line ("2900 Markham Rd A5, Scarborough, ON
  // M1X 0C8"). When the city is stored separately, the first segment is the
  // street part - otherwise the locality would be repeated inside
  // streetAddress. With no city stored, the line is used as-is.
  const line = (settings.siteAddress || '').trim();
  const street = settings.siteCity && line.includes(',')
    ? line.split(',')[0].trim()
    : line;
  const country = (settings.siteCountry || '').trim();

  return compact({
    '@type': 'PostalAddress',
    streetAddress: street,
    addressLocality: settings.siteCity,
    addressCountry: COUNTRY_CODES[country.toLowerCase()] || country,
  });
}

function socialProfiles(settings) {
  return [
    settings.siteFacebookUrl,
    settings.siteInstagramUrl,
    settings.siteTiktokUrl,
    settings.siteLinkedinUrl,
    settings.siteYoutubeUrl,
    settings.siteXUrl,
  ].filter(Boolean);
}

// The store itself. ElectronicsStore is a LocalBusiness subtype, so this one
// node covers both the "who runs this site" and "physical shop" questions an
// agent asks.
export function organizationSchema(settings = {}, origin = '') {
  const hours = parseOpeningHours(settings.siteBusinessHours);
  const address = postalAddress(settings);
  return compact({
    '@context': SCHEMA,
    '@type': 'ElectronicsStore',
    '@id': `${origin}/#organization`,
    name: settings.businessName || settings.siteName,
    description: settings.siteTagline,
    url: origin || undefined,
    logo: settings.siteLogoUrl || buildAbsoluteUrl('/og-default.png', origin),
    image: settings.siteLogoUrl || buildAbsoluteUrl('/og-default.png', origin),
    telephone: settings.sitePhone,
    email: settings.siteEmail,
    address: Object.keys(address).length > 1 ? address : undefined,
    hasMap: settings.siteGoogleMapUrl,
    openingHoursSpecification: hours ? [hours] : undefined,
    currenciesAccepted: settings.defaultCurrency,
    sameAs: socialProfiles(settings),
  });
}

// Site identity + the search box agents and Google use to query the catalogue.
export function websiteSchema(settings = {}, origin = '') {
  return compact({
    '@context': SCHEMA,
    '@type': 'WebSite',
    '@id': `${origin}/#website`,
    name: settings.siteName,
    description: settings.siteTagline,
    url: origin || undefined,
    publisher: origin ? { '@id': `${origin}/#organization` } : undefined,
    potentialAction: origin
      ? {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${origin}/search?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        }
      : undefined,
  });
}

// Offers are emitted only when the price is actually public. With
// contact_for_price / login_to_view_price / inquiry_only there is no price to
// state, and an Offer without one is worse than no Offer at all.
function productOffer(product, settings, origin) {
  const price = product.sale_price || product.price || product.regular_price;
  if (product.price_visibility !== 'show_price' || !price) return null;
  return compact({
    '@type': 'Offer',
    url: buildAbsoluteUrl(`/product/${product.slug}`, origin),
    price: String(price),
    priceCurrency: settings.defaultCurrency || 'CAD',
    availability: AVAILABILITY[product.stock_status] || undefined,
    seller: origin ? { '@id': `${origin}/#organization` } : undefined,
  });
}

export function productSchema(product, settings = {}, origin = '') {
  if (!product?.slug || !product?.name) return null;
  const images = (product.images || [])
    .map((i) => i.image_url)
    .filter(Boolean);
  const offer = productOffer(product, settings, origin);

  return compact({
    '@context': SCHEMA,
    '@type': 'Product',
    '@id': `${buildAbsoluteUrl(`/product/${product.slug}`, origin)}#product`,
    name: product.name,
    description: product.short_description,
    sku: product.sku,
    mpn: product.model,
    image: images,
    url: buildAbsoluteUrl(`/product/${product.slug}`, origin),
    category: product.category_name,
    brand: product.brand_name
      ? { '@type': 'Brand', name: product.brand_name }
      : undefined,
    offers: offer || undefined,
  });
}

// `items` mirrors the visible <Breadcrumb> trail: [{ label, to }], last crumb
// usually without a `to`.
export function breadcrumbSchema(items = [], origin = '') {
  const usable = items.filter((i) => i && i.label);
  if (usable.length < 2) return null;
  return {
    '@context': SCHEMA,
    '@type': 'BreadcrumbList',
    itemListElement: usable.map((item, i) =>
      compact({
        '@type': 'ListItem',
        position: i + 1,
        name: item.label,
        item: item.to ? buildAbsoluteUrl(item.to, origin) : undefined,
      })
    ),
  };
}

// A listing page's products, in the order shown. Position is 1-based across
// the whole result set so paginated pages don't all claim to start at 1.
export function itemListSchema(products = [], { origin = '', name, startPosition = 1 } = {}) {
  const usable = products.filter((p) => p?.slug && p?.name);
  if (!usable.length) return null;
  return compact({
    '@context': SCHEMA,
    '@type': 'ItemList',
    name,
    numberOfItems: usable.length,
    itemListElement: usable.map((p, i) => ({
      '@type': 'ListItem',
      position: startPosition + i,
      url: buildAbsoluteUrl(`/product/${p.slug}`, origin),
      name: p.name,
    })),
  });
}

export function blogPostingSchema(blog, settings = {}, origin = '') {
  if (!blog?.slug || !blog?.title) return null;
  return compact({
    '@context': SCHEMA,
    '@type': 'BlogPosting',
    '@id': `${buildAbsoluteUrl(`/blog/${blog.slug}`, origin)}#post`,
    headline: blog.title,
    description: blog.excerpt,
    image: blog.featured_image_url,
    datePublished: blog.published_at,
    dateModified: blog.updated_at || blog.published_at,
    author: blog.author_name
      ? { '@type': 'Person', name: blog.author_name }
      : origin
        ? { '@id': `${origin}/#organization` }
        : undefined,
    publisher: origin ? { '@id': `${origin}/#organization` } : undefined,
    mainEntityOfPage: buildAbsoluteUrl(`/blog/${blog.slug}`, origin),
  });
}

// `faqs` is the flat list of { question, answer } the FAQ page renders.
export function faqSchema(faqs = []) {
  const usable = faqs.filter((f) => f?.question && f?.answer);
  if (!usable.length) return null;
  return {
    '@context': SCHEMA,
    '@type': 'FAQPage',
    mainEntity: usable.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };
}
